// LocalStorage service with type safety

import { STORAGE_KEYS } from '@/constants'

class StorageService {
  // Generic get method
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key)
      if (!item) return null
      return JSON.parse(item) as T
    } catch (error) {
      console.error(`Error getting item ${key} from localStorage:`, error)
      return null
    }
  }

  // Generic set method
  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.error(`Error setting item ${key} to localStorage:`, error)
    }
  }

  // Remove item
  remove(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`Error removing item ${key} from localStorage:`, error)
    }
  }

  // Clear all storage
  clear(): void {
    try {
      localStorage.clear()
    } catch (error) {
      console.error('Error clearing localStorage:', error)
    }
  }

  // Auth specific methods
  getAuthToken(): string | null {
    return this.get<string>(STORAGE_KEYS.AUTH_TOKEN)
  }

  setAuthToken(token: string): void {
    this.set(STORAGE_KEYS.AUTH_TOKEN, token)
  }

  getRefreshToken(): string | null {
    return this.get<string>(STORAGE_KEYS.REFRESH_TOKEN)
  }

  setRefreshToken(token: string): void {
    this.set(STORAGE_KEYS.REFRESH_TOKEN, token)
  }

  removeAuthTokens(): void {
    this.remove(STORAGE_KEYS.AUTH_TOKEN)
    this.remove(STORAGE_KEYS.REFRESH_TOKEN)
    this.remove(STORAGE_KEYS.IS_LOGGED_IN)
  }

  getUser<T>(): T | null {
    return this.get<T>(STORAGE_KEYS.USER)
  }

  setUser<T>(user: T): void {
    this.set(STORAGE_KEYS.USER, user)
  }

  removeUser(): void {
    this.remove(STORAGE_KEYS.USER)
  }

  isLoggedIn(): boolean {
    return this.get<boolean>(STORAGE_KEYS.IS_LOGGED_IN) === true
  }

  setLoggedIn(value: boolean): void {
    this.set(STORAGE_KEYS.IS_LOGGED_IN, value)
  }

  // Theme
  getTheme(): string | null {
    return this.get<string>(STORAGE_KEYS.THEME)
  }

  setTheme(theme: string): void {
    this.set(STORAGE_KEYS.THEME, theme)
  }

  // Sidebar state
  getSidebarCollapsed(): boolean {
    return this.get<boolean>(STORAGE_KEYS.SIDEBAR_COLLAPSED) ?? false
  }

  setSidebarCollapsed(collapsed: boolean): void {
    this.set(STORAGE_KEYS.SIDEBAR_COLLAPSED, collapsed)
  }
}

// Export singleton instance
export const storageService = new StorageService()
